import { faClipboardCheck, faListCheck, faListOl, faUserCheck } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Meta } from 'src/components/Meta';
import { Stack } from 'src/components/Stack';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import { AdaptiveSideMenu } from 'src/shared/AdaptiveSideMenu/AdaptiveSideMenu';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';

import { AssessmentsIndexPage } from './AssessmentsIndexPage';
import { AssessmentsSequencesPage } from './AssessmentsSequencesPage';
import { CompletedUserAssessmentsPage } from './CompletedUserAssessmentsPage';
import { LeadsPage } from './LeadsPage';

/**
 * This component is the main layout for the Trainer Assessments page.
 * It contains the side menu and the content for the Trainer Assessments page.
 * @returns The Trainer Assessments Layout component
 */
const AssessmentsLayout = ({ tabName = '' }: { tabName?: string }) => {
  const { session } = useSession();
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessments',
  });
  const { t: tAssessment } = useTranslation('translation', {
    keyPrefix: 'assessment',
  });

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const activeTab = tabName || '';

  const tabs = [
    {
      icon: faListCheck,
      label: t('assessments'),
      value: '',
      component: <AssessmentsIndexPage />,
    },
    {
      icon: faClipboardCheck,
      label: t('completedLeads'),
      value: 'leads',
      component: <LeadsPage />,
    },
    {
      icon: faUserCheck,
      label: t('completedUsers'),
      value: 'completed',
      component: <CompletedUserAssessmentsPage />,
    },
    {
      icon: faListOl,
      label: tAssessment('assessmentSequences'),
      value: 'sequences',
      component: <AssessmentsSequencesPage />,
    },
  ];

  const currentTab = tabs.find((tab) => tab.value === activeTab) ?? tabs[0];

  const activeTabContent = currentTab.component;
  const pageTitle = currentTab.label;

  const assessmentsContent = (
    <>
      <Meta title={pageTitle} brand={session?.brand} />
      <AppFrameConfig
        id="assessments"
        containerMaxWidth={'xl'}
        title={pageTitle}
      />
      <Stack direction={mdDown ? 'column' : 'row'}>
        <AdaptiveSideMenu
          navItems={tabs}
          activeValue={activeTab}
          onValueChange={(value) => navigate(`/trainer/assessments/${value}`)}
          collapsible={true}
          title={t('assessments')}
          id="assessments"
        />
        <Box sx={{ width: '100%', transition: 'all 0.3s ease' }}>
          {activeTabContent}
        </Box>
      </Stack>
    </>
  );

  return assessmentsContent;
};

export default AssessmentsLayout;
